﻿let _profUserId = 'default'; try { require('@react-native-async-storage/async-storage').default.getItem('userToken').then(function(t){if(t)_profUserId=t.slice(0,16);}).catch(function(){}); } catch(e) {}
import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  SafeAreaView,
  StatusBar,
  TextInput,
  FlatList,
  ActivityIndicator,
  Alert,
  Modal,
  Dimensions,
  Animated,
  RefreshControl,
  Image,
} from 'react-native';
import { apiFetch } from '../../utils/apiClient';
import { Ionicons } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useProfessional } from '../../context/ProfessionalContext';
import { useProfessionalOnboarding } from '../../context/ProfessionalOnboardingContext';
import { useGeneral } from '../../context/GeneralContext';

// Import Unified API
import { 


  searchAllJobs, 
  getAPIStatus, 
  getConfiguredAPIs,
  getJobTypeStatistics
} from '../../services/unifiedJobAPI';
import useProfile from '../../hooks/useProfile';
import ProfileManagementService from '../../services/ProfileManagementService';
import PROFILE_KEYS from '../../constants/profileKeys';

// === API CONFIG ===
const API_BASE = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
const { width, height } = Dimensions.get('window');

/**
 * Transforms a job from the unifiedJobAPI into the structure
 * expected by the EntryLevelJobCard component.
 */
const transformApiJob = (job) => {
  return {
    // Data from API
    id: job.id || job.jobId,
    jobId: job.jobId || job.id,
    title: job.title,
    company: job.companyName || 'Company',
    companyLogo: job.logo || `https://logo.clearbit.com/${job.companyName?.toLowerCase().replace(/\s/g, '')}.com`,
    location: job.location,
    salary: job.salary,
    type: job.jobType || 'Full-time',
    remote: job.isRemote,
    isRemote: job.isRemote,
    posted: job.posted || 'Recently',
    description: job.description,
    requirements: job.requirements || [],
    skills: job.skills || [],
    matchScore: job.matchScore || 70, // Default match score
    featured: job.featured,
    urgent: job.urgent,
    dataSource: job.dataSource, // JSearch, Google Jobs, Adzuna

    // --- Fields required by the UI (defaulted or faked) ---
    experienceLevel: 'Entry Level', // We are searching for this
    industry: 'Engineering',     // We are searching for this
    salaryRange: { 
      min: job.salary?.min || 20000, 
      max: job.salary?.max || 35000 
    },
    
    // API data doesn't provide these, so set to null/false
    // The UI will be updated to conditionally hide these.
    graduateProgram: String(job.title ?? '').toLowerCase().includes('graduate'), // Best guess
    trainingProvided: String(job.description ?? '').toLowerCase().includes('training'), // Best guess
    
    companyInfo: {
      size: 'N/A',
      industry: 'Engineering',
      founded: 'N/A',
      rating: 4.0, // Default rating
      culture: 'N/A',
    },
    careerPath: null, // API cannot provide this
  };
};


export default function EntryLevelJobsScreen({ navigation }) {
  // ── Profile integration (auto-injected) ──
  const profileCtx = useProfile();
  // === Live API Data ===
  const [userId, setUserId] = React.useState('default');
  React.useEffect(() => {
    AsyncStorage.getItem('userToken').then(t => { if (t) setUserId(t.slice(0, 16)); }).catch(() => {});
  }, []);
  React.useEffect(() => {
    if (userId === 'default') return;
    const _ck = `professional_${_profUserId}_entryleveljobsscreen`;
    apiFetch('/api/data/jobs?experience_level=entry&sort=created_at&order=desc&limit=50').then(d => {
      if (d && !d.error) {
        const arr = Array.isArray(d) ? d : (d.data || d.results || []);
        AsyncStorage.setItem(_ck, JSON.stringify(arr)).catch(() => {});
      }
    }).catch(() => {});
  }, [userId]);

  // Context hooks
  const {
    userData = {},
    savedJobs = [],
    jobApplications = [],
    saveJob,
    unsaveJob,
    applyToJob,
    trackUserAction
  } = useProfessional() || {};

  const {
    profileData = {},
    scoring = {},
    aiMatching = {},
    runAIProfileAnalysis,
    calculateJobMatch
  } = useProfessionalOnboarding() || {};
  
  const { showToast, trackUserAction: trackGeneralAction } = useGeneral() || {};

  // State management
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTab, setSelectedTab] = useState('all');
  const [isLoading, setIsLoading] = useState(true); // Set to true for initial load
  const [refreshing, setRefreshing] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [applyingJobId, setApplyingJobId] = useState(null);
  const [selectedFilters, setSelectedFilters] = useState({
    experienceLevel: 'all',
    industry: 'all',
    salaryRange: 'all',
    remote: false,
    trainingProvided: false,
  });

  // Animation values
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const slideAnim = useRef(new Animated.Value(50)).current;

  // Entry-level job tabs
  const entryLevelTabs = [
    { id: 'all', title: 'All Engineering', icon: 'school-outline' },
    { id: 'recommended', title: 'For You', icon: 'star-outline' },
    { id: 'graduate', title: 'Graduate', icon: 'ribbon-outline' },
    { id: 'saved', title: 'Saved', icon: 'bookmark-outline' },
    { id: 'applied', title: 'Applied', icon: 'checkmark-circle-outline' },
  ];

  // ✅ FIX: Initialize state as empty arrays instead of mock data
  const [allEntryLevelJobs, setAllEntryLevelJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [apiError, setApiError] = useState(null);
  const [apiSources, setApiSources] = useState({});

  /**
   * ✅ NEW: Main function to load jobs from all sources
   */
  const loadJobs = async () => {
    if (refreshing) {
      setIsLoading(false);
    } else {
      setIsLoading(true);
    }
    setApiError(null);
    
    try {
      const configuredAPIs = getConfiguredAPIs();
      console.log(`📡 ${configuredAPIs.total} APIs configured for Entry Level`);

      if (configuredAPIs.total > 0) {
        const apiResult = await loadJobsFromAPI();
        
        // Transform API data to fit the UI
        const transformedJobs = apiResult.jobs.map(transformApiJob);
        
        setAllEntryLevelJobs(transformedJobs);
        setFilteredJobs(transformedJobs); // Initially, show all
        setApiSources(apiResult.sources);
        
        console.log(`✅ Loaded and transformed ${transformedJobs.length} entry-level engineering jobs.`);

      } else {
        console.warn('⚠️ No APIs configured. Displaying empty screen.');
        setApiError('No job search APIs are configured.');
      }

    } catch (error) {
      console.error('Error loading jobs:', error);
      setApiError(error.message);
    } finally {
      setIsLoading(false);
      setRefreshing(false);
    }
  };


  /**
   * ✅ UPDATED: Fetches data from the Unified API
   */
  const loadJobsFromAPI = async () => {
    try {
      console.log('🔍 Fetching entry-level ENGINEERING jobs from all configured APIs...');

      // ✅ FIX: New query to find all types of junior/graduate engineers
      const baseQuery = '"junior engineer" OR "graduate engineer" OR "entry level engineer"';
      const disciplines = '"civil" OR "mechanical" OR "electrical" OR "chemical" OR "aerospace" OR "biomedical" OR "architectural" OR "structural"';
      const finalQuery = `(${baseQuery}) AND (${disciplines})`;
      
      const searchParams = {
        query: finalQuery,
        location: profileData?.personal?.location || 'United Kingdom',
        remote: selectedFilters.remote,
        page: 1, // Add pagination later if needed
      };

      const results = await searchAllJobs(searchParams);

      if (results.success) {
        console.log(`✅ Loaded ${results.total} entry-level jobs from API`);

        Object.entries(results.sources).forEach(([source, data]) => {
          if (data.enabled) {
            const status = data.error ? `❌ Error: ${data.error}` : `✅ ${data.count} jobs`;
            console.log(`   ${source}: ${status}`);
          }
        });

        return {
          jobs: results.jobs,
          sources: results.sources,
        };
      } else {
        throw new Error(results.error || 'Failed to fetch jobs from APIs');
      }
    } catch (error) {
      console.error('Error loading from APIs:', error);
      setApiError(error.message);
      return {
        jobs: [],
        sources: {},
      };
    }
  };

  // Enhanced job filtering
  const filterJobs = (query = '', tab = 'all') => {
    let filtered = [...allEntryLevelJobs];

    // Filter by search query
    if (query.trim()) {
      const searchLower = query.toLowerCase();
      filtered = filtered.filter(job =>
        String(job.title ?? '').toLowerCase().includes(searchLower) ||
        String(job.company ?? '').toLowerCase().includes(searchLower) ||
        String(job.location ?? '').toLowerCase().includes(searchLower) ||
        String(job.description ?? '').toLowerCase().includes(searchLower) ||
        String(job.industry ?? '').toLowerCase().includes(searchLower) ||
        String(job.experienceLevel ?? '').toLowerCase().includes(searchLower) ||
        (job.skills && job.skills.some(skill =>
          skill.toLowerCase().includes(searchLower)
        ))
      );
    }

    // Filter by tab
    switch (tab) {
      case 'recommended':
        filtered = filtered.filter(job => job.matchScore >= 85);
        break;
      case 'graduate':
        filtered = filtered.filter(job => job.graduateProgram);
        break;
      case 'saved':
        filtered = filtered.filter(job => isJobSaved(job.id));
        break;
      case 'applied':
        filtered = filtered.filter(job => isJobApplied(job.id));
        break;
    }

    // Apply additional filters
    if (selectedFilters.experienceLevel !== 'all') {
      filtered = filtered.filter(job =>
        String(job.experienceLevel ?? '').toLowerCase().includes(String(selectedFilters.experienceLevel ?? '').toLowerCase())
      );
    }

    if (selectedFilters.salaryRange !== 'all') {
      filtered = filtered.filter(job => {
        const minSalary = job.salaryRange?.min || 0;
        switch (selectedFilters.salaryRange) {
          case '15-20k':
            return minSalary >= 15000 && minSalary <= 20000;
          case '20-25k':
            return minSalary >= 20000 && minSalary <= 25000;
          case '25k+':
            return minSalary >= 25000;
          default:
            return true;
        }
      });
    }

    if (selectedFilters.industry !== 'all') {
      filtered = filtered.filter(job =>
        String(job.industry ?? '').toLowerCase() === String(selectedFilters.industry ?? '').toLowerCase()
      );
    }

    if (selectedFilters.remote) {
      filtered = filtered.filter(job => job.remote || job.isRemote);
    }

    if (selectedFilters.trainingProvided) {
      filtered = filtered.filter(job => job.trainingProvided);
    }

    setFilteredJobs(filtered);
  };

  // Enhanced search handler
  const handleSearch = (query) => {
    setSearchQuery(query);
    filterJobs(query, selectedTab);
  };

  // Tab press handler
  const handleTabPress = (tab) => {
    setSelectedTab(tab.id);
    filterJobs(searchQuery, tab.id);

    if (trackUserAction) {
      trackUserAction('entry_level_jobs_tab_change', { tab: tab.id });
    }
  };

  // Initialize component
  useEffect(() => {
    console.log('🚀 ENTRY LEVEL JOBS - Component mounted');
    
    // ✅ FIX: Load jobs from API on mount
    loadJobs();

    // Start animations
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 800,
        useNativeDriver: true,
      }),
      Animated.timing(slideAnim, {
        toValue: 0,
        duration: 800,
        useNativeDriver: true,
      }),
    ]).start();

  }, []); // Run only once on mount

  // Refresh handler
  const onRefresh = async () => {
    setRefreshing(true);
    // ✅ FIX: Reload jobs from API
    await loadJobs();
  };
  
  // (The rest of your functions: handleSaveJob, isJobSaved, isJobApplied, formatSalary, etc. are fine)
  // ... (Paste all your existing functions here, from handleJobApply down to renderFilterModal) ...
  // ...
  
  // (handleJobApply function - no changes needed)
const handleJobApply = async (job) => {
  try {
    if (!job || !job.id) {
      console.error('❌ ENTRY LEVEL JOBS - No job data provided for application');
      Alert.alert('Error', 'Job information is not available. Please try again.');
      return;
    }

    console.log('🚀 ENTRY LEVEL JOBS - Starting application process for:', job.title);
    setApplyingJobId(job.id);
      // Check if already applied
      const isAlreadyApplied = Array.isArray(jobApplications) &&
        jobApplications.some(app => app && (app.jobId === job.id || app.jobId === job.jobId));

      if (isAlreadyApplied) {
        Alert.alert('Already Applied', 'You have already applied to this entry-level position.');
        setApplyingJobId(null);
        return;
      }

      // FIXED: Navigate to SubmitForJobsScreen with comprehensive entry-level job data
      const navigationParams = {
        jobId: job.id || job.jobId,
        id: job.id || job.jobId,
        job: job,
        jobData: job,
        source: 'EntryLevelJobsScreen',
        timestamp: new Date().toISOString(),
        jobType: 'Entry Level',
        experienceLevel: job.experienceLevel,
        industry: job.industry,
        salaryRange: job.salaryRange,
        careerPath: job.careerPath,
        graduateProgram: job.graduateProgram,
        trainingProvided: job.trainingProvided,
        isEntryLevelPosition: true,
      };

      console.log('🧭 ENTRY LEVEL JOBS - Navigating to SubmitForJobs with params:', navigationParams);

      try {
        // Primary navigation attempt
        navigation.navigate('SubmitForJobs', navigationParams);
        console.log('✅ ENTRY LEVEL JOBS - Navigation to SubmitForJobs successful');

        // Track the action
        if (trackUserAction) {
          trackUserAction('entry_level_jobs_apply_navigation', {
            jobId: job.id,
            jobTitle: job.title,
            company: job.company,
            source: 'EntryLevelJobsScreen',
            experienceLevel: job.experienceLevel,
            industry: job.industry,
            graduateProgram: job.graduateProgram,
            salary: job.salary
          });
        }

      } catch (navigationError) {
        console.warn('⚠️ ENTRY LEVEL JOBS - Primary navigation failed, trying alternatives:', navigationError);

        // Fallback navigation attempts
        const fallbackScreens = [
          'JobApplicationScreen',
          'JobApplication',
          'SubmitForJobs',
          'ApplyToJob',
          'ApplicationForm',
          'GraduateApplication'
        ];

        let navigationSuccessful = false;

        for (const screenName of fallbackScreens) {
          try {
            console.log(`🔄 ENTRY LEVEL JOBS - Trying fallback navigation to: ${screenName}`);
            navigation.navigate(screenName, navigationParams);
            console.log(`✅ ENTRY LEVEL JOBS - Fallback navigation to ${screenName} successful`);
            navigationSuccessful = true;
            break;
          } catch (fallbackError) {
            console.warn(`❌ ENTRY LEVEL JOBS - Fallback to ${screenName} failed:`, fallbackError);
          }
        }

        if (!navigationSuccessful) {
          console.error('❌ ENTRY LEVEL JOBS - All navigation attempts failed');
          Alert.alert(
            'Navigation Error',
            'Unable to open application form. Please try again or contact support.',
            [
              {
                text: 'Try Again',
                onPress: () => handleJobApply(job),
              },
              {
                text: 'Cancel',
                style: 'cancel',
              },
            ]
          );
        }
      }

    } catch (error) {
      console.error('❌ ENTRY LEVEL JOBS - Error in handleJobApply:', error);
      Alert.alert('Error', 'An error occurred while applying. Please try again.');
    } finally {
      setApplyingJobId(null);
    }
  };

  // (handleSaveJob function - no changes needed)
  const handleSaveJob = async (job) => {
    try {
      const isSaved = isJobSaved(job.id);

      if (isSaved) {
        if (unsaveJob && typeof unsaveJob === 'function') {
          await unsaveJob(job.id);
          if (showToast) {
            showToast('Entry-level job removed from saved', 'info');
          }
        }
      } else {
        if (saveJob && typeof saveJob === 'function') {
          await saveJob(job);
          if (showToast) {
            showToast('Entry-level job saved successfully!', 'success');
          }
        }
      }

      if (trackUserAction) {
        trackUserAction('entry_level_jobs_save_toggle', {
          jobId: job.id,
          action: isSaved ? 'unsave' : 'save'
        });
      }
    } catch (error) {
      console.error('❌ ENTRY LEVEL JOBS - Error saving job:', error);
      if (showToast) {
        showToast('Failed to save entry-level job', 'error');
      }
    }
  };

  // (isJobSaved function - no changes needed)
  const isJobSaved = (jobId) => {
    return Array.isArray(savedJobs) && savedJobs.some(savedJob => savedJob.id === jobId);
  };

  // (isJobApplied function - no changes needed)
  const isJobApplied = (jobId) => {
    return Array.isArray(jobApplications) && jobApplications.some(app => app.jobId === jobId);
  };

  // (formatSalary function - no changes needed)
  const formatSalary = (salary) => {
    if (typeof salary === 'string') return salary;
    if (salary?.min && salary?.max) {
      return `£${(salary.min / 1000).toFixed(0)}k - £${(salary.max / 1000).toFixed(0)}k`;
    }
    return 'Competitive';
  };
  
  // --- RENDER FUNCTIONS ---
  
  // (renderHeader function - no changes needed)
  const renderHeader = () => (
    <View style={styles.header}>
      <TouchableOpacity
        style={styles.backButton}
        onPress={() => navigation.goBack()}
      >
        <Ionicons name="arrow-back" size={24} color="#ffffff" />
      </TouchableOpacity>

      <View style={styles.headerTitleContainer}>
        <Text style={styles.headerTitle}>Entry-Level Jobs</Text>
        <Text style={styles.headerSubtitle}>{filteredJobs.length} opportunities for graduates</Text>
      </View>

      <TouchableOpacity
        style={styles.headerActionButton}
        onPress={() => setShowFilters(true)}
      >
        <Ionicons name="options" size={20} color="#ffffff" />
      </TouchableOpacity>
    </View>
  );

  // (renderSearchBar function - no changes needed)
  const renderSearchBar = () => (
    <View style={styles.searchContainer}>
      <View style={styles.searchInputContainer}>
        <Ionicons name="search" size={20} color="#64748B" />
        <TextInput
          style={styles.searchInput}
          placeholder="Search entry-level jobs, skills, or companies..."
          value={searchQuery}
          onChangeText={handleSearch}
          placeholderTextColor="#94a3b8"
        />
        {searchQuery.length > 0 && (
          <TouchableOpacity onPress={() => handleSearch('')}>
            <Ionicons name="close-circle" size={20} color="#64748B" />
          </TouchableOpacity>
        )}
      </View>
    </View>
  );

  // (renderGraduateBenefits function - no changes needed)
  const renderGraduateBenefits = () => (
    <View style={styles.benefitsContainer}>
      <Text style={styles.benefitsTitle}>Graduate Benefits</Text>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.benefitsScrollContainer}
      >
        {[
          { icon: 'school', title: 'Training Programs', color: '#ff6b6b' },
          { icon: 'people', title: 'Mentorship', color: '#4ecdc4' },
          { icon: 'trending-up', title: 'Career Growth', color: '#45b7d1' },
          { icon: 'ribbon', title: 'Graduate Schemes', color: '#96ceb4' },
        ].map((benefit, index) => (
          <View key={index} style={[styles.benefitCard, { borderColor: benefit.color }]}>
            <View style={[styles.benefitIcon, { backgroundColor: benefit.color }]}>
              <Ionicons name={benefit.icon} size={16} color="#ffffff" />
            </View>
            <Text style={styles.benefitTitle}>{benefit.title}</Text>
          </View>
        ))}
      </ScrollView>
    </View>
  );

  // (renderTabs function - updated to use filteredJobs.length for 'all')
  const renderTabs = () => (
    <View style={styles.tabsContainer}>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.tabsScrollContainer}
      >
        {entryLevelTabs.map((tab) => {
          // Calculate counts based on the *currently* filtered list, except for 'all'
          let jobCount = 0;
          switch(tab.id) {
            case 'all':
              jobCount = filteredJobs.length; // Count from the main filtered list
              break;
            case 'recommended':
              jobCount = filteredJobs.filter(job => job.matchScore >= 85).length;
              break;
            case 'graduate':
              jobCount = filteredJobs.filter(job => job.graduateProgram).length;
              break;
            case 'saved':
              // Use context for saved/applied as it's the source of truth
              jobCount = savedJobs.filter(savedJob => filteredJobs.some(j => j.id === savedJob.id)).length;
              break;
            case 'applied':
              jobCount = jobApplications.filter(app => filteredJobs.some(j => j.id === app.jobId)).length;
              break;
            default:
              jobCount = 0;
          }

          return (
            <TouchableOpacity
              key={tab.id}
              style={[
                styles.tab,
                selectedTab === tab.id && styles.activeTab,
              ]}
              onPress={() => handleTabPress(tab)}
            >
              <Ionicons
                name={tab.icon}
                size={16}
                color={selectedTab === tab.id ? "#ffffff" : "#64748B"}
              />
              <Text style={[
                styles.tabText,
                selectedTab === tab.id && styles.activeTabText,
              ]}>
                {tab.title}
              </Text>
              {jobCount > 0 && (
                <View style={[
                  styles.tabBadge,
                  selectedTab === tab.id && styles.activeTabBadge,
                ]}>
                  <Text style={[
                    styles.tabBadgeText,
                    selectedTab === tab.id && styles.activeTabBadgeText,
                  ]}>
                    {jobCount}
                  </Text>
                </View>
              )}
            </TouchableOpacity>
          );
        })}
      </ScrollView>
    </View>
  );

  /**
   * ✅ UPDATED: Render card with conditional fields
   * This card will now conditionally hide sections like "Career Path"
   * if the API data (transformed) does not provide them.
   */
  const renderEntryLevelJobCard = ({ item: job }) => {
    const isSaved = isJobSaved(job.id);
    const isApplied = isJobApplied(job.id);
    const isApplying = applyingJobId === job.id;

    return (
      <Animated.View
        style={[
          styles.jobCard,
          {
            opacity: fadeAnim,
            transform: [{ translateY: slideAnim }],
          },
        ]}
      >
        {/* Job badges */}
        <View style={styles.jobBadges}>
          <View style={[styles.badge, styles.entryLevelBadge]}>
            <Ionicons name="school" size={12} color="#ffffff" />
            <Text style={styles.badgeText}>Entry Level</Text>
          </View>
          {job.featured && (
            <View style={[styles.badge, styles.featuredBadge]}>
              <Ionicons name="star" size={12} color="#ffffff" />
              <Text style={styles.badgeText}>Featured</Text>
            </View>
          )}
          {job.urgent && (
            <View style={[styles.badge, styles.urgentBadge]}>
              <Ionicons name="flash" size={12} color="#ffffff" />
              <Text style={styles.badgeText}>Urgent</Text>
            </View>
          )}
          {(job.remote || job.isRemote) && (
            <View style={[styles.badge, styles.remoteBadge]}>
              <Ionicons name="home" size={12} color="#ffffff" />
              <Text style={styles.badgeText}>Remote</Text>
            </View>
          )}
          {/* ✅ CONDITIONAL: Only show if true */}
          {job.graduateProgram && (
            <View style={[styles.badge, styles.graduateBadge]}>
              <Ionicons name="ribbon" size={12} color="#ffffff" />
              <Text style={styles.badgeText}>Graduate</Text>
            </View>
          )}
          {/* ✅ CONDITIONAL: Only show if true */}
          {job.trainingProvided && (
            <View style={[styles.badge, styles.trainingBadge]}>
              <Ionicons name="book" size={12} color="#ffffff" />
              <Text style={styles.badgeText}>Training</Text>
            </View>
          )}
        </View>

        {/* Company info */}
        <View style={styles.jobHeader}>
          <View style={styles.companyLogoContainer}>
            {job.companyLogo ? (
              <Image
                source={{ uri: (job.companyLogo && typeof job.companyLogo=="string" && job.companyLogo.startsWith("http")) ? job.companyLogo : undefined }}
                style={styles.companyLogo}
                onError={() => console.warn('Failed to load company logo for:', job.company)}
              />
            ) : (
              <View style={styles.companyLogoPlaceholder}>
                <Text style={styles.companyLogoText}>
                  {job.company.charAt(0)}
                </Text>
              </View>
            )}
          </View>

          <View style={styles.jobInfo}>
            <Text style={styles.jobTitle} numberOfLines={2}>{job.title}</Text>
            <Text style={styles.companyName}>{job.company}</Text>

            <View style={styles.jobMeta}>
              <View style={styles.metaItem}>
                <Ionicons name="location" size={12} color="#64748B" />
                <Text style={styles.metaText}>{job.location}</Text>
              </View>
              <View style={styles.metaItem}>
                <Ionicons name="cash" size={12} color="#64748B" />
                <Text style={styles.metaText}>{formatSalary(job.salaryRange) || job.salary}</Text>
              </View>
            </View>
          </View>

          <TouchableOpacity
            style={styles.saveButton}
            onPress={() => handleSaveJob(job)}
          >
            <Ionicons
              name={isSaved ? "bookmark" : "bookmark-outline"}
              size={20}
              color={isSaved ? "#ff6b6b" : "#64748B"}
            />
          </TouchableOpacity>
        </View>

        {/* Job description */}
        <Text style={styles.jobDescription} numberOfLines={2}>
          {job.description}
        </Text>

        {/* Entry-level specific info */}
        <View style={styles.entryLevelInfoContainer}>
          <View style={styles.entryLevelInfoItem}>
            <Ionicons name="school-outline" size={14} color="#ff6b6b" />
            <Text style={styles.entryLevelInfoText}>{job.experienceLevel}</Text>
          </View>
          <View style={styles.entryLevelInfoItem}>
            <Ionicons name="business-outline" size={14} color="#ff6b6b" />
            <Text style={styles.entryLevelInfoText}>{job.industry}</Text>
          </View>
          {/* ✅ CONDITIONAL: Only show if rating exists */}
          {job.companyInfo?.rating && (
            <View style={styles.entryLevelInfoItem}>
              <Ionicons name="star-outline" size={14} color="#ff6b6b" />
              <Text style={styles.entryLevelInfoText}>{job.companyInfo.rating} rating</Text>
            </View>
          )}
        </View>

        {/* ✅ CONDITIONAL: Only show if careerPath exists */}
        {job.careerPath && (
          <View style={styles.careerPathContainer}>
            <Text style={styles.careerPathTitle}>Career Development</Text>
            <Text style={styles.careerPathText}>
              Next role: {job.careerPath.nextRole} in {job.careerPath.timeframe}
            </Text>
          </View>
        )}

        {/* Skills required */}
        {job.skills && job.skills.length > 0 && (
          <View style={styles.skillsContainer}>
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              contentContainerStyle={styles.skillsScrollContainer}
            >
              {job.skills.slice(0, 4).map((skill, index) => (
                <View key={index} style={styles.skillTag}>
                  <Text style={styles.skillText}>{skill}</Text>
                </View>
              ))}
            </ScrollView>
          </View>
        )}

        {/* Match score and actions */}
        <View style={styles.jobFooter}>
          <View style={styles.matchContainer}>
            <View style={styles.matchScore}>
              <Text style={styles.matchText}>{job.matchScore}% match</Text>
            </View>
            <Text style={styles.postedTime}>{job.posted}</Text>
          </View>

          <View style={styles.actionButtons}>
            <TouchableOpacity
              style={styles.detailsButton}
              onPress={() => {
                try {
                  navigation.navigate('JobDetails', {
                    jobId: job.id,
                    id: job.id,
                    job: job,
                    source: 'EntryLevelJobsScreen'
                  });
                } catch (error) {
                  console.error('❌ ENTRY LEVEL JOBS - Navigation to JobDetailsScreen failed:', error);
                  Alert.alert('Error', 'Unable to view job details. Please try again.');
                }
              }}
            >
              <Ionicons name="information-circle-outline" size={16} color="#ff6b6b" />
              <Text style={styles.detailsButtonText}>Details</Text>
            </TouchableOpacity>

            {/* FIXED: Apply Now button with proper navigation */}
            <TouchableOpacity
              style={[
                styles.applyButton,
                isApplied && styles.appliedButton,
                isApplying && styles.applyingButton,
              ]}
              onPress={() => handleJobApply(job)}
              disabled={isApplied || isApplying}
            >
              {isApplying ? (
                <>
                  <ActivityIndicator size={20} color="#ffffff" />
                  <Text style={styles.applyButtonText}>Applying...</Text>
                </>
              ) : (
                <>
                  {isApplied ? (
                    <Ionicons name="checkmark-circle" size={16} color="#ffffff" />
                  ) : (
                    <Ionicons name="paper-plane" size={16} color="#ffffff" />
                  )}
                  <Text style={[styles.applyButtonText, isApplied && styles.appliedButtonText]}>
                    {isApplied ? 'Applied' : 'Apply Now'}
                  </Text>
                </>
              )}
            </TouchableOpacity>
          </View>
        </View>
      </Animated.View>
    );
  };

  // (renderEmptyState function - no changes needed)
  const renderEmptyState = () => (
    <View style={styles.emptyContainer}>
      <Ionicons name="school-outline" size={64} color="#94a3b8" />
      <Text style={styles.emptyTitle}>No Entry-Level Jobs Found</Text>
      <Text style={styles.emptyMessage}>
        Try adjusting your search criteria or check back later for new graduate opportunities.
      </Text>
      <TouchableOpacity
        style={styles.refreshButton}
        onPress={() => {
          setSearchQuery('');
          setSelectedTab('all');
          loadJobs(); // Rerun the API call
        }}
      >
        <Ionicons name="refresh" size={16} color="#ff6b6b" />
        <Text style={styles.refreshButtonText}>Reset Filters</Text>
      </TouchableOpacity>
    </View>
  );

  // (renderFilterModal function - no changes needed)
  const renderFilterModal = () => (
    <Modal
      visible={showFilters}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={() => setShowFilters(false)}
    >
      <SafeAreaView style={styles.modalContainer}>
        <View style={styles.modalHeader}>
          <Text style={styles.modalTitle}>Entry-Level Job Filters</Text>
          <TouchableOpacity
            style={styles.modalCloseButton}
            onPress={() => setShowFilters(false)}
          >
            <Ionicons name="close" size={24} color="#64748B" />
          </TouchableOpacity>
        </View>

        <ScrollView
          style={styles.modalContent}
          contentContainerStyle={styles.modalContentContainer}
          showsVerticalScrollIndicator={false}
        >
          {/* Experience Level Filter */}
          <View style={styles.filterSection}>
            <Text style={styles.filterTitle}>Experience Level</Text>
            <View style={styles.filterOptions}>
              {['all', 'entry level', 'graduate', 'trainee'].map((level) => (
                <TouchableOpacity
                  key={level}
                  style={[
                    styles.filterOption,
                    selectedFilters.experienceLevel === level && styles.selectedFilterOption,
                  ]}
                  onPress={() => setSelectedFilters(prev => ({ ...prev, experienceLevel: level }))}
                >
                  <Text style={[
                    styles.filterOptionText,
                    selectedFilters.experienceLevel === level && styles.selectedFilterOptionText,
                  ]}>
                    {level === 'all' ? 'All Levels' : level.charAt(0).toUpperCase() + level.slice(1)}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>

          {/* Salary Range Filter */}
          <View style={styles.filterSection}>
            <Text style={styles.filterTitle}>Salary Range</Text>
            <View style={styles.filterOptions}>
              {['all', '15-20k', '20-25k', '25k+'].map((range) => (
                <TouchableOpacity
                  key={range}
                  style={[
                    styles.filterOption,
                    selectedFilters.salaryRange === range && styles.selectedFilterOption,
                  ]}
                  onPress={() => setSelectedFilters(prev => ({ ...prev, salaryRange: range }))}
                >
                  <Text style={[
                    styles.filterOptionText,
                    selectedFilters.salaryRange === range && styles.selectedFilterOptionText,
                  ]}>
                    {range === 'all' ? 'All Salaries' : `£${range}`}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>

          {/* Industry Filter */}
          <View style={styles.filterSection}>
            <Text style={styles.filterTitle}>Industry</Text>
            <View style={styles.filterOptions}>
              {['all', 'technology', 'marketing', 'finance', 'design', 'data & analytics', 'engineering'].map((industry) => (
                <TouchableOpacity
                  key={industry}
                  style={[
                    styles.filterOption,
                    selectedFilters.industry === industry && styles.selectedFilterOption,
                  ]}
                  onPress={() => setSelectedFilters(prev => ({ ...prev, industry: industry }))}
                >
                  <Text style={[
                    styles.filterOptionText,
                    selectedFilters.industry === industry && styles.selectedFilterOptionText,
                  ]}>
                    {industry === 'all' ? 'All Industries' : industry.charAt(0).toUpperCase() + industry.slice(1)}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>

          {/* Special Filters */}
          <View style={styles.filterSection}>
            <Text style={styles.filterTitle}>Special Features</Text>

            <TouchableOpacity
              style={[
                styles.toggleOption,
                selectedFilters.remote && styles.selectedToggleOption,
              ]}
              onPress={() => setSelectedFilters(prev => ({ ...prev, remote: !prev.remote }))}
            >
              <Ionicons
                name={selectedFilters.remote ? "checkbox" : "square-outline"}
                size={20}
                color={selectedFilters.remote ? "#ff6b6b" : "#64748B"}
              />
              <Text style={styles.toggleOptionText}>Remote Work Only</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[
                styles.toggleOption,
                selectedFilters.trainingProvided && styles.selectedToggleOption,
              ]}
              onPress={() => setSelectedFilters(prev => ({ ...prev, trainingProvided: !prev.trainingProvided }))}
            >
              <Ionicons
                name={selectedFilters.trainingProvided ? "checkbox" : "square-outline"}
                size={20}
                color={selectedFilters.trainingProvided ? "#ff6b6b" : "#64748B"}
              />
              <Text style={styles.toggleOptionText}>Training Provided</Text>
            </TouchableOpacity>
          </View>

          {/* Apply Filters Button */}
          <TouchableOpacity
            style={styles.applyFiltersButton}
            onPress={() => {
              filterJobs(searchQuery, selectedTab);
              setShowFilters(false);
            }}
          >
            <Text style={styles.applyFiltersButtonText}>Apply Filters</Text>
          </TouchableOpacity>
        </ScrollView>
      </SafeAreaView>
    </Modal>
  );

  return (
    <SafeAreaView style={styles.container}>
      <StatusBar barStyle="light-content" backgroundColor="#ff6b6b" />

      {renderHeader()}

      {/* ✅ FIX: Show loading indicator when loading */}
      {isLoading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size={36} color="#ff6b6b" />
          <Text style={styles.loadingText}>Loading Junior Engineering Jobs...</Text>
        </View>
      ) : (
        <ScrollView
          style={styles.content}
          showsVerticalScrollIndicator={false}
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor="#ff6b6b" />
          }
        >
          {renderSearchBar()}
          {renderGraduateBenefits()}
          {renderTabs()}

          <View style={styles.jobsContainer}>
            <Text style={styles.jobsTitle}>
              {entryLevelTabs.find(tab => tab.id === selectedTab)?.title || 'Entry-Level Jobs'}
            </Text>

            {filteredJobs.length > 0 ? (
              <FlatList
                data={filteredJobs}
                renderItem={renderEntryLevelJobCard}
                keyExtractor={(item) => item.id}
                scrollEnabled={false} // The outer ScrollView handles scrolling
                showsVerticalScrollIndicator={false}
              />
            ) : (
              renderEmptyState()
            )}
          </View>
        </ScrollView>
      )}

      {renderFilterModal()}
    </SafeAreaView>
  );
}

// ... (Paste your *entire* existing styles object here)
const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#0e4e96',
  },
  // ✅ ADDED: Loading container styles
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f8fafc',
  },
  loadingText: {
    marginTop: 10,
    fontSize: 16,
    color: '#64748B',
    fontWeight: '500',
  },
  // (All your other styles from the original file)
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 16,
    backgroundColor: '#0e4e96',
  },
  backButton: {
    padding: 8,
    borderRadius: 20,
    backgroundColor: 'rgba(255,255,255,0.15)',
  },
  headerTitleContainer: {
    flex: 1,
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#ffffff',
    marginBottom: 2,
  },
  headerSubtitle: {
    fontSize: 12,
    color: 'rgba(255,255,255,0.8)',
    fontWeight: '500',
  },
  headerActionButton: {
    padding: 8,
    borderRadius: 20,
    backgroundColor: 'rgba(255,255,255,0.15)',
  },
  content: {
    flex: 1,
    backgroundColor: '#f8fafc',
  },
  searchContainer: {
    backgroundColor: '#ffffff',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
  },
  searchInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f8fafc',
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  searchInput: {
    flex: 1,
    fontSize: 16,
    color: '#1e293b',
    marginLeft: 12,
  },
  benefitsContainer: {
    backgroundColor: '#ffffff',
    paddingVertical: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
  },
  benefitsTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#1e293b',
    marginBottom: 16,
    paddingHorizontal: 20,
  },
  benefitsScrollContainer: {
    paddingHorizontal: 20,
  },
  benefitCard: {
    alignItems: 'center',
    backgroundColor: '#ffffff',
    borderRadius: 12,
    padding: 12,
    marginRight: 12,
    borderWidth: 2,
    minWidth: 80,
  },
  benefitIcon: {
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 8,
  },
  benefitTitle: {
    fontSize: 11,
    color: '#1e293b',
    fontWeight: '600',
    textAlign: 'center',
  },
  tabsContainer: {
    backgroundColor: '#ffffff',
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
  },
  tabsScrollContainer: {
    paddingHorizontal: 20,
  },
  tab: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f8fafc',
    borderRadius: 20,
    paddingHorizontal: 16,
    paddingVertical: 10,
    marginRight: 12,
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  activeTab: {
    backgroundColor: '#ff6b6b',
    borderColor: '#ff6b6b',
  },
  tabText: {
    fontSize: 14,
    color: '#64748B',
    fontWeight: '500',
    marginLeft: 6,
  },
  activeTabText: {
    color: '#ffffff',
  },
  tabBadge: {
    backgroundColor: '#e2e8f0',
    borderRadius: 10,
    paddingHorizontal: 6,
    paddingVertical: 2,
    marginLeft: 6,
  },
  activeTabBadge: {
    backgroundColor: 'rgba(255,255,255,0.3)',
  },
  tabBadgeText: {
    fontSize: 10,
    color: '#64748B',
    fontWeight: '600',
  },
  activeTabBadgeText: {
    color: '#ffffff',
  },
  jobsContainer: {
    backgroundColor: '#ffffff',
    marginTop: 8,
    paddingHorizontal: 20,
    paddingVertical: 20,
  },
  jobsTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#1e293b',
    marginBottom: 16,
  },
  jobCard: {
    backgroundColor: '#ffffff',
    borderRadius: 16,
    padding: 20,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: '#e2e8f0',
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 3.84,
    elevation: 5,
  },
  jobBadges: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 12,
  },
  badge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    marginRight: 8,
    marginBottom: 4,
  },
  entryLevelBadge: {
    backgroundColor: '#ff6b6b',
  },
  featuredBadge: {
    backgroundColor: '#f59e0b',
  },
  urgentBadge: {
    backgroundColor: '#ef4444',
  },
  remoteBadge: {
    backgroundColor: '#10b981',
  },
  graduateBadge: {
    backgroundColor: '#8b5cf6',
  },
  trainingBadge: {
    backgroundColor: '#06b6d4',
  },
  badgeText: {
    fontSize: 10,
    color: '#ffffff',
    fontWeight: '600',
    marginLeft: 4,
  },
  jobHeader: {
    flexDirection: 'row',
    marginBottom: 12,
  },
  companyLogoContainer: {
    marginRight: 12,
  },
  companyLogo: {
    width: 48,
    height: 48,
    borderRadius: 12,
    backgroundColor: '#f1f5f9',
  },
  companyLogoPlaceholder: {
    width: 48,
    height: 48,
    borderRadius: 12,
    backgroundColor: '#ff6b6b',
    justifyContent: 'center',
    alignItems: 'center',
  },
  companyLogoText: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#ffffff',
  },
  jobInfo: {
    flex: 1,
  },
  jobTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: '#1e293b',
    marginBottom: 4,
    lineHeight: 22,
  },
  companyName: {
    fontSize: 14,
    fontWeight: '600',
    color: '#ff6b6b',
    marginBottom: 8,
  },
  jobMeta: {
    flexDirection: 'row',
  },
  metaItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 16,
  },
  metaText: {
    fontSize: 12,
    color: '#64748B',
    marginLeft: 4,
    fontWeight: '500',
  },
  saveButton: {
    padding: 8,
  },
  jobDescription: {
    fontSize: 14,
    color: '#64748B',
    lineHeight: 20,
    marginBottom: 12,
  },
  entryLevelInfoContainer: {
    flexDirection: 'row',
    marginBottom: 12,
  },
  entryLevelInfoItem: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fef2f2',
    borderRadius: 12,
    paddingHorizontal: 8,
    paddingVertical: 4,
    marginRight: 8,
    borderWidth: 1,
    borderColor: '#fecaca',
  },
  entryLevelInfoText: {
    fontSize: 11,
    color: '#dc2626',
    fontWeight: '600',
    marginLeft: 4,
  },
  careerPathContainer: {
    backgroundColor: '#fef2f2',
    borderRadius: 12,
    padding: 12,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#fecaca',
  },
  careerPathTitle: {
    fontSize: 12,
    fontWeight: '600',
    color: '#dc2626',
    marginBottom: 4,
  },
  careerPathText: {
    fontSize: 11,
    color: '#dc2626',
    lineHeight: 16,
  },
  skillsContainer: {
    marginBottom: 16,
  },
  skillsScrollContainer: {
    paddingRight: 20,
  },
  skillTag: {
    backgroundColor: '#fef2f2',
    borderRadius: 16,
    paddingHorizontal: 12,
    paddingVertical: 6,
    marginRight: 8,
    borderWidth: 1,
    borderColor: '#fecaca',
  },
  skillText: {
    fontSize: 12,
    color: '#dc2626',
    fontWeight: '500',
  },
  jobFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  matchContainer: {
    flex: 1,
  },
  matchScore: {
    backgroundColor: '#fef2f2',
    borderRadius: 12,
    paddingHorizontal: 8,
    paddingVertical: 4,
    alignSelf: 'flex-start',
    marginBottom: 4,
  },
  matchText: {
    fontSize: 12,
    color: '#dc2626',
    fontWeight: '600',
  },
  postedTime: {
    fontSize: 11,
    color: '#94a3b8',
    fontWeight: '500',
  },
  actionButtons: {
    flexDirection: 'row',
  },
  detailsButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fef2f2',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 12,
    marginRight: 8,
    borderWidth: 1,
    borderColor: '#fecaca',
  },
  detailsButtonText: {
    fontSize: 12,
    color: '#ff6b6b',
    fontWeight: '600',
    marginLeft: 4,
  },
  applyButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#ff6b6b',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 12,
  },
  appliedButton: {
    backgroundColor: '#10b981',
  },
  applyingButton: {
    backgroundColor: '#94a3b8',
  },
  applyButtonText: {
    fontSize: 12,
    color: '#ffffff',
    fontWeight: '600',
    marginLeft: 4,
  },
  appliedButtonText: {
    color: '#ffffff',
  },
  emptyContainer: {
    alignItems: 'center',
    paddingVertical: 40,
  },
  emptyTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: '#1e293b',
    marginTop: 16,
    marginBottom: 8,
  },
  emptyMessage: {
    fontSize: 14,
    color: '#64748B',
    textAlign: 'center',
    lineHeight: 20,
    marginBottom: 24,
    paddingHorizontal: 20,
  },
  refreshButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fef2f2',
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#fecaca',
  },
  refreshButtonText: {
    fontSize: 14,
    color: '#ff6b6b',
    fontWeight: '600',
    marginLeft: 8,
  },
  modalContainer: {
    flex: 1,
    backgroundColor: '#ffffff',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#1e293b',
  },
  modalCloseButton: {
    padding: 8,
  },
  modalContent: {
    flex: 1,
    paddingHorizontal: 20,
  },
  modalContentContainer: { // Added for modal scroll content
    paddingBottom: 40,
  },
  filterSection: {
    marginVertical: 20,
  },
  filterTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1e293b',
    marginBottom: 12,
  },
  filterOptions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  filterOption: {
    backgroundColor: '#f8fafc',
    borderWidth: 1,
    borderColor: '#e2e8f0',
    borderRadius: 20,
    paddingHorizontal: 16,
    paddingVertical: 10,
    marginRight: 8,
    marginBottom: 8,
  },
  selectedFilterOption: {
    backgroundColor: '#ff6b6b',
    borderColor: '#ff6b6b',
  },
  filterOptionText: {
    fontSize: 14,
    color: '#64748B',
    fontWeight: '500',
  },
  selectedFilterOptionText: {
    color: '#ffffff',
  },
  toggleOption: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
  },
  selectedToggleOption: {
    backgroundColor: '#fef2f2',
  },
  toggleOptionText: {
    fontSize: 14,
    color: '#1e293b',
    fontWeight: '500',
    marginLeft: 12,
  },
  applyFiltersButton: {
    backgroundColor: '#ff6b6b',
    borderRadius: 12,
    paddingVertical: 16,
    alignItems: 'center',
    marginVertical: 20,
  },
  applyFiltersButtonText: {
    fontSize: 16,
    color: '#ffffff',
    fontWeight: '600',
  },
});